/*
 * ttg-cart-reconciler.js — Warenkorb-Aufräumer für Konfigurator-Zuschläge.
 * ---------------------------------------------------------------------------
 * Entfernt „Waisen"-Zuschläge und gleicht Zuschlagsmengen an die Hauptmenge an,
 * sobald sich der Warenkorb ändert:
 *   - Hauptartikel entfernt (Gruppe hat keine main-Zeile mehr) → alle
 *     Zuschlagszeilen derselben _ttg_group entfernen (quantity 0).
 *   - Hauptmenge geändert (z. B. 2 → 1) → Zuschlagsmengen angleichen
 *     (1 Hauptartikel = 1× jeder zugehörige Zuschlag).
 *
 * Verknüpfung: verstecktes Line-Item-Property _ttg_group (Haupt + Zuschlag),
 * _ttg_role = 'surcharge' an Zuschlagszeilen (gesetzt vom Theme-Adapter).
 * NORMALE Produkte (ohne _ttg_group) werden nie angefasst.
 *
 * Integration ins Horizon-Theme: hört auf das globale Event `cart:update`
 * (document) und nutzt dieselbe Section-Rendering-Maschinerie wie das Theme —
 * ein eigenes `cart:update` mit source='ttg-reconciler' lässt die
 * cart-items-component beide Oberflächen (/cart-Seite UND Drawer) morphen.
 *
 * WICHTIG — SERVER-REALITÄT (keine harte Garantie): Das ist reine Client-
 * Bequemlichkeit. Express-Checkout (Shop Pay, Apple/Google Pay) und jeder
 * Nicht-Browser-Pfad umgehen dieses JS — dort greift der Aufräumer NICHT.
 * Eine harte Integrität („Zuschlag existiert nie ohne Hauptartikel") ginge nur
 * serverseitig über eine Cart-/Checkout-Validation- bzw. Cart-Transform-
 * Function — und Custom-App-Functions sind Shopify-Plus-only. Auf diesem
 * Basic-Store bleibt es daher bewusst bei dieser Browser-Bequemlichkeit.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';
  if (window.__ttgCartReconciler) return;   // Einmal-Init (theme.liquid lädt global)
  window.__ttgCartReconciler = true;

  var EVENT = 'cart:update';
  var SELF = 'ttg-reconciler';               // Marker gegen Selbst-Retrigger (Loop-Schutz)
  var running = false, rerun = false, timer = null;

  function prop(item, key) { return item && item.properties ? item.properties[key] : undefined; }
  function groupOf(item) { return prop(item, '_ttg_group'); }
  function isSurcharge(item) { return prop(item, '_ttg_role') === 'surcharge'; }
  function route(name, fallback) { return (window.Theme && Theme.routes && Theme.routes[name]) || fallback; }

  /**
   * Nötige Mengen-Korrekturen aus dem aktuellen Cart ableiten. Rückgabe:
   * [{ key, quantity }] NUR für Zuschlagszeilen, deren Menge nicht der
   * Hauptmenge ihrer _ttg_group entspricht (Hauptartikel weg → Ziel 0).
   * Normale Zeilen (ohne _ttg_group) bleiben komplett außen vor.
   */
  function computeChanges(cart) {
    var groups = {};
    (cart.items || []).forEach(function (it) {
      var g = groupOf(it);
      if (!g) return;
      groups[g] = groups[g] || { mainQty: 0, surcharges: [] };
      if (isSurcharge(it)) groups[g].surcharges.push(it);
      else groups[g].mainQty += it.quantity || 0;
    });
    var changes = [];
    Object.keys(groups).forEach(function (g) {
      var grp = groups[g];
      grp.surcharges.forEach(function (sc) {
        var target = grp.mainQty;            // 1 Hauptartikel = 1 Zuschlag; 0 → entfernen
        if ((sc.quantity || 0) !== target) changes.push({ key: sc.key, quantity: target });
      });
    });
    return changes;
  }

  /** Section-IDs aller Cart-Komponenten (Seite + Drawer) — für das Re-Rendering. */
  function sectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      var id = el.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  /**
   * Eigenes cart:update auf document feuern → die Theme-Komponenten morphen
   * daraus beide Oberflächen. source=SELF, damit unser Handler NICHT erneut
   * anspringt (Loop-Schutz greift hier, nicht nur über das Guard-Flag).
   */
  function dispatchUpdate(cart, sections) {
    var ev = new Event(EVENT, { bubbles: true });
    ev.detail = {
      resource: cart,
      sourceId: SELF,
      data: { source: SELF, sections: sections || {}, itemCount: cart ? cart.item_count : undefined },
    };
    document.dispatchEvent(ev);
  }

  /** Ein Abgleich-Durchlauf: fetch Cart → berechnen → korrigieren → re-rendern. */
  async function reconcileOnce() {
    var cart = await fetch(route('cart_url', '/cart') + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
    if (!cart || !Array.isArray(cart.items)) return;

    var changes = computeChanges(cart);
    if (!changes.length) return;             // Normalfall: nichts zu tun → kein Request

    var ids = sectionIds();
    var url = route('cart_change_url', '/cart/change');
    if (!/\.js$/.test(url)) url += '.js';

    var last = null;
    // Sequentiell per stabilem Line-Key (kein Index-Shift, eindeutig). Sections
    // erst im LETZTEN Call anfordern → nur ein einziges Re-Render, kein Flackern.
    for (var i = 0; i < changes.length; i++) {
      var c = changes[i];
      var body = { id: c.key, quantity: c.quantity };
      if (i === changes.length - 1 && ids.length) {
        body.sections = ids.join(',');
        body.sections_url = window.location.pathname;
      }
      try {
        var res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) { console.warn('[ttg-reconciler] change HTTP ' + res.status); continue; }
        last = await res.json();
      } catch (e) { console.warn('[ttg-reconciler] change fehlgeschlagen:', e); }
    }
    if (last) dispatchUpdate(last, last.sections || {});
  }

  /**
   * Serialisierung (Mutex + rerun): läuft nie zwei Abgleiche parallel; kommt
   * während eines Laufs ein neues Update, wird EIN weiterer Lauf nachgezogen.
   * Jeder Lauf liest den Cart frisch → idempotent, kein Doppel-Request/422.
   */
  async function run() {
    if (running) { rerun = true; return; }
    running = true;
    try {
      do { rerun = false; await reconcileOnce(); } while (rerun);
    } finally { running = false; }
  }

  function onCartUpdate(event) {
    // Eigene Dispatches ignorieren (Loop-Schutz).
    try { if (event && event.detail && event.detail.data && event.detail.data.source === SELF) return; } catch (e) {}
    // Kurze Debounce: Theme-Morph zuerst zu Ende laufen lassen + Bursts koaleszieren.
    if (timer) clearTimeout(timer);
    timer = setTimeout(run, 150);
  }

  document.addEventListener(EVENT, onCartUpdate);

  // Boot-Abgleich: vorbestehende Waisen-Zuschläge auch OHNE vorheriges cart:update
  // bereinigen. Szenario: Hauptartikel in einer früheren Session (oder Express-Pfad)
  // entfernt, dann /cart frisch geladen — dort feuert kein cart:update, der Aufräumer
  // lief sonst nie an. Nur auf der /cart-Seite (kein /cart.js-Fetch auf jeder Seite;
  // Drawer-Fälle deckt weiterhin cart:update ab). Nutzt denselben debounced run()
  // (Mutex/rerun + Loop-Schutz) — kein Doppel-Lauf, wenn das Theme doch ein initiales
  // cart:update feuert.
  function bootReconcile() {
    var path = (window.location.pathname || '').replace(/\/+$/, '');
    if (!/\/cart$/.test(path)) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(run, 150);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootReconcile);
  } else {
    bootReconcile();
  }
})();
